import React, { memo } from 'react';
import { Badge, Box, Divider, Flex, Text } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import colors from '../../../../styles/colors';
import { RequisitionDetailViewModel } from './utils/RequisitionDetailsViewModel';

type RequisitionHeaderProps = Pick<
  RequisitionDetailViewModel,
  | 'requisitionId'
  | 'statusLabel'
  | 'statusColorScheme'
  | 'isFullTime'
  | 'isSeasonal'
  | 'partTimeHoursLabel'
  | 'wageLabel'
  | 'effectiveDateLabel'
  | 'seasonalEndDateLabel'
>;

const BADGE_STYLE = {
  color: colors.persianBlue,
  background: '#263DBA1A',
} as const;

export const RequisitionHeader = memo(
  ({
    requisitionId,
    statusLabel,
    statusColorScheme,
    isFullTime,
    isSeasonal,
    partTimeHoursLabel,
    wageLabel,
    effectiveDateLabel,
    seasonalEndDateLabel,
  }: RequisitionHeaderProps) => {
    const { t } = useTranslation('requisition');

    return (
      <Box>
        <Flex mt="16px" align="center" flexWrap="wrap" gridGap="8px">
          <Text fontSize={16} fontWeight={700} mr="4px" data-testid="requisition-details-title">
            {t('requisitionDetails')}
          </Text>

          {isSeasonal && (
            <Badge variant="subtle" colorScheme="blue" {...BADGE_STYLE} data-testid="seasonal-badge">
              {t('seasonal')}
            </Badge>
          )}

          <Badge variant="subtle" colorScheme="blue" {...BADGE_STYLE} data-testid="full-time-badge">
            {isFullTime
              ? t('fullTime')
              : `${t('partTime')}${partTimeHoursLabel ? `: ${partTimeHoursLabel} ${t('partTimeHoursPerWeek')}` : ''}`}
          </Badge>

          {statusLabel && (
            <Badge variant="subtle" colorScheme={statusColorScheme} data-testid="status-badge">
              {t(`requisition:${statusLabel}`)}
            </Badge>
          )}
        </Flex>

        <Divider mt="16px" />

        <Flex mt="12px" align="center" flexWrap="wrap" justifyContent="space-between">
          <Flex align="center" gridGap="6px">
            <Text fontSize="14px" fontWeight={700}>
              {t('requisitionID')}
            </Text>
            <Text fontSize="14px" data-testid="requisition-id-value">
              {requisitionId}
            </Text>
          </Flex>

          {effectiveDateLabel && (
            <Flex align="center" gridGap="6px">
              <Text fontSize="14px" fontWeight={700}>
                {t('effectiveTime')}
              </Text>
              <Text fontSize="14px" data-testid="effective-date-value">
                {effectiveDateLabel}
              </Text>
            </Flex>
          )}

          {wageLabel && (
            <Flex align="center" gridGap="6px">
              <Text fontSize="14px" fontWeight={700}>
                {t('wages')}
              </Text>
              <Text fontSize="14px" data-testid="wage-value">
                {wageLabel}
              </Text>
            </Flex>
          )}
        </Flex>

        {seasonalEndDateLabel && (
          <Flex mt="8px" align="center" gridGap="6px">
            <Text fontSize="14px" fontWeight={700}>
              {t('seasonalEndDate')}
            </Text>
            <Text fontSize="14px" data-testid="seasonal-end-date-value">
              {seasonalEndDateLabel}
            </Text>
          </Flex>
        )}
      </Box>
    );
  },
);

RequisitionHeader.displayName = 'RequisitionHeader';
